import React, { useState } from 'react';
import { WITHDRAW_API } from 'constants/payloads/tournaments/withdraw';
import { GetUserEventRegistrationQuery } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { useApiGateway } from 'hooks/useApi';
import CheckIcon from 'svg/CheckIcon';
import { Button } from 'components/Button';
import InfoAlert from 'components/InfoAlert';
import Header from './Header';
import { RegisterProps, Steps } from './types';

interface Props extends Pick<RegisterProps, 'setSteps' | 'event'> {
  registrations: GetUserEventRegistrationQuery;
  onWithdrawSuccess: () => void;
}

export default function WithdrawFromTournament({
  setSteps,
  event,
  registrations,
  onWithdrawSuccess,
}: Props) {
  const eventGroupRegistrations = registrations.eventGroupRegistrations ?? [];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(eventGroupRegistrations.map((r) => r.id)),
  );

  const { post, isLoading } = useApiGateway(WITHDRAW_API);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const registrationIds = [...selectedIds];
    if (registrationIds.length === 0) return;

    try {
      await post({ payload: { registrationIds } });
      onWithdrawSuccess();
    } catch (e) {
      console.error('Failed to withdraw from tournament', e);
    }
  };

  const now = Date.now();
  const isBeforeDeadline =
    !event.registrationDeadlineDateTime ||
    new Date(event.registrationDeadlineDateTime).getTime() > now;

  return (
    <div className="tournament-register-form flex h-full flex-col items-start">
      <div className="flex h-full w-full flex-auto flex-col overflow-y-auto">
        <Header
          title={`Withdraw from ${getEventExperience(event).noun}`}
          showBack
          showBackAction={() => setSteps(Steps.AddEvent)}
          className="px-6 pt-6"
        />
        <div className="px-6 pb-6">
          <p className="typography-product-body mt-ds-xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Choose the event(s) you want to withdraw from.
          </p>
          <div className="mt-ds-xl flex flex-col gap-3">
            {eventGroupRegistrations.map((registration) => {
              const isSelected = selectedIds.has(registration.id);
              return (
                <button
                  key={registration.id}
                  type="button"
                  onClick={() => toggleSelection(registration.id)}
                  className="flex w-full items-center justify-between rounded-xl border border-color-border-input-lightmode px-4 py-4 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode"
                >
                  <span className="typography-product-subheading text-left text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {registration.group.title || registration.group.formatCustomName || 'Event'}
                  </span>
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
                      isSelected
                        ? 'bg-color-bg-darkmode-primary'
                        : 'border border-color-border-input-lightmode dark:border-color-border-input-darkmode'
                    }`}
                  >
                    {isSelected && (
                      <CheckIcon className="h-3 w-3 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {isBeforeDeadline && (
            <InfoAlert
              title="You will be refunded if the registration deadline has not passed."
              className="mt-ds-xl"
            />
          )}
        </div>
      </div>
      <div className="flex w-full flex-col gap-3 px-6 pb-6">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setSteps(Steps.AddEvent)}
          type="button"
        >
          Go back
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleConfirm}
          loading={isLoading}
          disabled={selectedIds.size === 0}
          type="button"
        >
          Confirm withdrawal
        </Button>
      </div>
    </div>
  );
}
